import { useState, useEffect, useRef } from "react";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { Button } from "@/components/_components/button";
import { cn } from "@/lib/utils";
import { inputStyles } from "theme/components/input";
import { textareaStyles } from "theme/components/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { selectStyles } from "theme/components/select";
import { Checkbox } from "@/components/_components/checkbox";
import { ChevronLeft, Save, FileText } from "lucide-react";
import { toast } from "react-hot-toast";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { customInstance } from "@/lib/axios-instance";
import { API_CONFIG } from "@/constants/api";
import { isAxiosError } from "axios";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface CandidateData {
  id: number;
  fullName: string;
  email: string;
  mobile: string;
  phone: string;
  totalExperience: string;
  relevantExperience: string;
  resumeLastUpdated: string;
  gender: string;
  jobStabilityRating: number;
  jobChangeInLast5Years: boolean;
  blackListed: boolean;
  maxTravelDistance: number;
  dateAvailable: string;
  noticePeriod: string;
  onboardingStatus: string;
  preferredLanguage: string;
  title: string;
  about: string;
  reasonForChange: string;
  completenessPercentage: number;
  // JSON fields as strings for textarea input
  agency: string;
  doNotContact: string;
  contactPreferences: string;
  jobboardSource: string;
  jobPreference: string;
  profileCompleteness: string;
  skills: string;
  employmentHistory: string;
  educations: string;
  certifications: string;
  socialLinks: string;
  languages: string;
  address: string;
  preferredLocations: string;
}

export default function EditCandidatePage() {
  return (
    <PageAccessGuard authority="ADMIN_TESTING_UPDATE">
      <EditCandidatePageContent />
    </PageAccessGuard>
  );
}

function EditCandidatePageContent() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetched = useRef(false);

  const [formData, setFormData] = useState<CandidateData>({
    id: 0,
    fullName: "",
    email: "",
    mobile: "",
    phone: "",
    totalExperience: "",
    relevantExperience: "",
    resumeLastUpdated: "",
    gender: "",
    jobStabilityRating: 0,
    jobChangeInLast5Years: false,
    blackListed: false,
    maxTravelDistance: 0,
    dateAvailable: "",
    noticePeriod: "",
    onboardingStatus: "",
    preferredLanguage: "",
    title: "",
    about: "",
    reasonForChange: "",
    completenessPercentage: 0,
    agency: "",
    doNotContact: "",
    contactPreferences: "",
    jobboardSource: "",
    jobPreference: "",
    profileCompleteness: "",
    skills: "",
    employmentHistory: "",
    educations: "",
    certifications: "",
    socialLinks: "",
    languages: "",
    address: "",
    preferredLocations: ""
  });

  // Fetch candidate data
  useEffect(() => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      fetchCandidate();
    }
  }, [id]);

  const fetchCandidate = async () => {
    if (!id) return;

    try {
      setIsLoading(true);

      const response = await customInstance<any>({
        url: `${API_CONFIG.ENDPOINTS.TEMP_CANDIDATE}/id/${id}`,
        method: 'GET'
      });

      const data = response.data;

      // Convert JSON objects to strings for editing
      const jsonFields = [
        'agency', 'doNotContact', 'contactPreferences', 'jobboardSource',
        'jobPreference', 'profileCompleteness', 'skills', 'employmentHistory',
        'educations', 'certifications', 'socialLinks', 'languages', 'address', 'preferredLocations'
      ];

      const processedData = { ...data };
      jsonFields.forEach(field => {
        if (typeof data[field] === 'object') {
          processedData[field] = JSON.stringify(data[field], null, 2);
        } else {
          processedData[field] = data[field] || "";
        }
      });

      setFormData(processedData);
    } catch (error) {
      console.error('Error fetching candidate:', error);
      hasFetched.current = false; // Reset on error to allow retry

      // Extract error message from API response
      let errorMessage = "Failed to load candidate";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to load candidate: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to load candidate: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to load candidate: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to load candidate: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof CandidateData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);

      // Parse JSON fields
      const jsonFields = [
        'agency', 'doNotContact', 'contactPreferences', 'jobboardSource',
        'jobPreference', 'profileCompleteness', 'skills', 'employmentHistory',
        'educations', 'certifications', 'socialLinks', 'languages', 'address', 'preferredLocations'
      ];

      const submitData: any = { ...formData };

      for (const field of jsonFields) {
        const fieldValue = formData[field as keyof CandidateData] as string;
        
        // Skip empty or whitespace-only fields
        if (!fieldValue || fieldValue.trim() === '') {
          submitData[field] = null;
          continue;
        }
        
        const trimmedValue = fieldValue.trim();
        
        try {
          submitData[field] = JSON.parse(trimmedValue);
        } catch (error) {
          // If it's a plain string (not JSON), try to convert it to appropriate format
          if (field === 'jobboardSource' && !trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) {
            // Convert plain string to jobboard object
            submitData[field] = { jobboardName: trimmedValue, sfdcId: "" };
            continue;
          }
          
          if (field === 'preferredLocations' && !trimmedValue.startsWith('[')) {
            // Convert plain string or comma-separated values to array
            const locations = trimmedValue.split(',').map(loc => loc.trim()).filter(loc => loc);
            submitData[field] = locations;
            continue;
          }
          
          console.error(`JSON parse error in ${field}:`, error);
          console.error(`Field content:`, trimmedValue);
          
          // Scroll to the problematic field
          const element = document.getElementById(field);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
          
          const errorMsg = error instanceof Error ? error.message : 'Please check the format.';
          toast.error(`Invalid JSON in ${field} field. ${errorMsg}`);
          return;
        }
      }

      await customInstance({
        url: `${API_CONFIG.ENDPOINTS.TEMP_CANDIDATE}/id/${id}`,
        method: 'PUT',
        data: submitData
      });

      toast.success("Candidate updated successfully!");
      navigate(ROUTES.TESTING_CANDIDATE_MGMT);
    } catch (error) {
      console.error('Error updating candidate:', error);

      // Extract error message from API response
      let errorMessage = "Failed to update candidate";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to update candidate: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to update candidate: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to update candidate: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to update candidate: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-6">{UI_MESSAGES.LOADING}</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.TESTING_CANDIDATE_MGMT)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Candidates
        </Button>

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Edit Candidate</h1>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Saving..." : "Update Candidate"}
          </Button>
        </div>

        {/* Personal Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label htmlFor="id">Candidate ID</label>
                <input
                  id="id"
                  value={formData.id}
                  disabled
                  className="bg-gray-50"
                />
              </div>
              <div>
                <label htmlFor="fullName">Full Name *</label>
                <input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Enter full name"
                  className={inputStyles}
                />
              </div>
              <div>
                <label htmlFor="title">Job Title *</label>
                <input
                  id="title"
                  value={formData.title}
                  className={inputStyles}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter job title"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={inputStyles}
                  placeholder="Enter email address"
                />
              </div>
              
              <div>
                <label htmlFor="phone">Mobile Phone (from phone) *</label>
                <input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={inputStyles}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label htmlFor="gender">Gender</label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('gender', e.target.value)}
                  className={selectStyles}
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>
              <div>
                <label htmlFor="preferredLanguage">Preferred Language</label>
                <select
                  id="preferredLanguage"
                  value={formData.preferredLanguage}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('preferredLanguage', e.target.value)}
                  className={selectStyles}
                >
                  <option value="">Select language</option>
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="German">German</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="resumeLastUpdated">Resume Last Updated</label>
                <input
                  id="resumeLastUpdated"
                  type="date"
                  value={formData.resumeLastUpdated}
                  className={inputStyles}
                  onChange={(e) => handleInputChange('resumeLastUpdated', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Information Section */}
        <Card>
          <CardHeader>
            <CardTitle>Professional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="totalExperience">Total Experience (Years)</label>
                <input
                  id="totalExperience"
                  value={formData.totalExperience}
                  className={inputStyles}
                  onChange={(e) => handleInputChange('totalExperience', e.target.value)}
                  placeholder="e.g., 5"
                />
              </div>
              <div>
                <label htmlFor="relevantExperience">Relevant Experience (Years)</label>
                <input
                  id="relevantExperience"
                  value={formData.relevantExperience}
                  className={inputStyles}
                  onChange={(e) => handleInputChange('relevantExperience', e.target.value)}
                  placeholder="e.g., 3"
                />
              </div>
              <div>
                <label htmlFor="noticePeriod">Notice Period</label>
                <Select value={formData.noticePeriod} onValueChange={(value) => handleInputChange('noticePeriod', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select notice period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Immediate">Immediate</SelectItem>
                    <SelectItem value="1 Week">1 Week</SelectItem>
                    <SelectItem value="2 Weeks">2 Weeks</SelectItem>
                    <SelectItem value="1 Month">1 Month</SelectItem>
                    <SelectItem value="2 Months">2 Months</SelectItem>
                    <SelectItem value="3 Months">3 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="jobStabilityRating">Job Stability Rating (1-10)</label>
                <input
                  id="jobStabilityRating"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.jobStabilityRating}
                  className={inputStyles}
                  onChange={(e) => handleInputChange('jobStabilityRating', Number.parseInt(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="dateAvailable">Date Available</label>
                <input
                  id="dateAvailable"
                  type="date"
                  value={formData.dateAvailable ? formData.dateAvailable.split('T')[0] : ''}
                  className={inputStyles}
                  onChange={(e) => handleInputChange('dateAvailable', e.target.value ? `${e.target.value}T00:00:00.000Z` : '')}
                />
              </div>
              <div>
                <label htmlFor="maxTravelDistance">Max Travel Distance (miles)</label>
                <input
                  id="maxTravelDistance"
                  type="number"
                  value={formData.maxTravelDistance}
                  className={inputStyles}
                  onChange={(e) => handleInputChange('maxTravelDistance', Number.parseInt(e.target.value) || 0)}
                  placeholder="e.g., 25"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="jobChangeInLast5Years"
                  checked={formData.jobChangeInLast5Years}
                  onCheckedChange={(checked) => handleInputChange('jobChangeInLast5Years', checked as boolean)}
                />
                <label htmlFor="jobChangeInLast5Years">Job change in last 5 years</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="blackListed"
                  checked={formData.blackListed}
                  onCheckedChange={(checked) => handleInputChange('blackListed', checked as boolean)}
                />
                <label htmlFor="blackListed">Blacklisted</label>
              </div>
            </div>

            <div>
              <label htmlFor="about">About / Summary</label>
              <textarea
                id="about"
                value={formData.about}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('about', e.target.value)}
                className={cn(textareaStyles, "min-h-[120px]")}
                placeholder="Brief professional summary..."
              />
            </div>

            <div>
              <label htmlFor="reasonForChange">Reason for Job Change</label>
              <textarea
                id="reasonForChange"
                value={formData.reasonForChange}
                onChange={(e) => handleInputChange('reasonForChange', e.target.value)}
                className={cn(textareaStyles, "min-h-[100px]")}
                placeholder="Why are you looking for a new opportunity?"
              />
            </div>
          </CardContent>
        </Card>

        {/* Structured Data Section */}
        <Card>
          <CardHeader>
            <CardTitle>Structured Information</CardTitle>
            <p className="text-sm text-gray-600">The following fields contain structured data in JSON format</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="agency">Agency Information</label>
                <textarea
                  id="agency"
                  value={formData.agency}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('agency', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='{"name": "Agency Name", "sfdcId": "ID"}'
                />
              </div>
              <div>
                <label htmlFor="jobboardSource">Jobboard Source</label>
                <textarea
                  id="jobboardSource"
                  value={formData.jobboardSource}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('jobboardSource', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='{"jobboardName": "Portal Name", "sfdcId": "ID"}'
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contactPreferences">Contact Preferences</label>
                <textarea
                  id="contactPreferences"
                  value={formData.contactPreferences}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('contactPreferences', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='{"email": true, "phone": true, "sms": true, "whatsapp": true}'
                />
              </div>
              <div>
                <label htmlFor="doNotContact">Do Not Contact</label>
                <textarea
                  id="doNotContact"
                  value={formData.doNotContact}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('doNotContact', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='{"email": false, "phone": false, "sms": false, "whatsapp": false}'
                />
              </div>
            </div>

            <div>
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                value={formData.address}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('address', e.target.value)}
                className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                placeholder='{"country": "USA", "state": "NY", "city": "New York", "zip": "10001"}'
              />
            </div>

            <div>
              <label htmlFor="jobPreference">Job Preferences</label>
              <textarea
                id="jobPreference"
                value={formData.jobPreference}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('jobPreference', e.target.value)}
                className={cn(textareaStyles, "min-h-[180px] font-mono text-sm")}
                placeholder='{"openToRelocate": false, "openToOnsite": false, "openToRemote": false, "titleMatchCriteria": ["Engineer", "Developer"]}'
              />
            </div>

            <div>
              <label htmlFor="skills">Skills</label>
              <textarea
                id="skills"
                value={formData.skills}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('skills', e.target.value)}
                className={cn(textareaStyles, "min-h-[150px] font-mono text-sm")}
                placeholder='[{"title": "Python", "level": "3"}, {"title": "JavaScript", "level": "2"}]'
              />
            </div>

            <div>
              <label htmlFor="employmentHistory">Employment History</label>
              <textarea
                id="employmentHistory"
                value={formData.employmentHistory}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('employmentHistory', e.target.value)}
                className={cn(textareaStyles, "min-h-[200px] font-mono text-sm")}
                placeholder='[{"employerName": "Company", "jobTitle": "Position", "startDate": "2020-01-01T00:00:00.000Z", "endDate": "2023-01-01T00:00:00.000Z", "currentlyWorking": false}]'
              />
            </div>

            <div>
              <label htmlFor="educations">Education</label>
              <textarea
                id="educations"
                value={formData.educations}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('educations', e.target.value)}
                className={cn(textareaStyles, "min-h-[200px] font-mono text-sm")}
                placeholder='[{"degree": "Bachelor of Science", "institute": "University Name", "startDate": "2016-01-01T00:00:00.000Z", "endDate": "2020-01-01T00:00:00.000Z"}]'
              />
            </div>

            <div>
              <label htmlFor="certifications">Certifications</label>
              <textarea
                id="certifications"
                value={formData.certifications}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('certifications', e.target.value)}
                className={cn(textareaStyles, "min-h-[150px] font-mono text-sm")}
                placeholder='[{"name": "AWS Certified", "issuingAuthority": "Amazon", "certificationNumber": "123456", "expirationDate": "2025-01-01T00:00:00.000Z"}]'
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="socialLinks">Social Links</label>
                <textarea
                  id="socialLinks"
                  value={formData.socialLinks}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('socialLinks', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='[{"name": "LinkedIn", "profileLink": "https://linkedin.com/in/username"}]'
                />
              </div>
              <div>
                <label htmlFor="languages">Languages</label>
                <textarea
                  id="languages"
                  value={formData.languages}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('languages', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='[{"name": "English", "proficiency": "Fluent"}]'
                />
              </div>
            </div>

            <div>
              <label htmlFor="preferredLocations">Preferred Locations</label>
              <textarea
                id="preferredLocations"
                value={formData.preferredLocations}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('preferredLocations', e.target.value)}
                className={cn(textareaStyles, "min-h-[100px] font-mono text-sm")}
                placeholder='["New York, NY, USA", "San Francisco, CA, USA"] or comma-separated: Portland, OR, Seattle, WA'
              />
              <p className="text-xs text-gray-500 mt-1">
                Accepts JSON array or comma-separated values (will be auto-converted)
              </p>
            </div>

            <div>
              <label htmlFor="profileCompleteness">Profile Completeness</label>
              <textarea
                id="profileCompleteness"
                value={formData.profileCompleteness}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('profileCompleteness', e.target.value)}
                className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                placeholder='[{"category": "Personal Information", "weightage": 10, "isComplete": true}]'
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}